import { useCallback, useEffect, useRef, useState } from "react";
import type { FC, FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { triggerBrowserDownload } from "components/control/recordingsMediaUtils";
import FunctionalButton from "components/layout/FunctionalButton";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import {
    ETHERNET_CONNECT_GREEN_BUTTON_CLASS,
    ETHERNET_CONNECT_GREEN_TITLE_CLASS,
    NETWORK_ROW_CONNECT_BUTTON_CLASS,
    NETWORK_ROW_CONNECT_TITLE_CLASS,
    NETWORK_ROW_DISCONNECT_BUTTON_CLASS,
    NETWORK_ROW_DISCONNECT_TITLE_CLASS,
} from "lib/consts/networkSettingsActionButtons";
import {
    getInsta360PlaybackUrl,
    getInsta360PreviewUrl,
    fetchInsta360RecordingBlob,
    getOpenSpaceSettings,
    getOpenSpaceUpload,
    listInsta360CameraFiles,
    listInsta360Recordings,
    postInsta360CameraTransfer,
    postOpenSpaceUpload,
    postOpenSpaceUploadCancel,
    downloadOpenSpaceNetworkLog,
    putOpenSpaceSettings,
    type Insta360CameraFile,
    type Insta360RecordingFile,
    type OpenSpaceUploadJob,
} from "lib/r2c2360Api";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { useInsta360Session } from "providers/Insta360SessionProvider";
import { useToast } from "providers/ToastProvider";

const DEFAULT_DOMAIN = "https://openspace.ai";

const INPUT_CLASS =
    "rounded-r5 border border-black60 bg-black100 px-3 py-2 text-h5 text-white outline-none focus:border-primary2";

function formatInsvSize(n: number): string {
    if (!Number.isFinite(n)) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatJobTime(iso: string | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
}

function InsvPreview({
    baseUrl,
    filename,
    alt,
}: {
    baseUrl: string;
    filename: string;
    alt: string;
}) {
    const [failed, setFailed] = useState(false);
    const src = getInsta360PreviewUrl(baseUrl, filename);
    if (failed) {
        return (
            <div
                className="h-12 w-20 shrink-0 rounded-r5 border border-black80 bg-black70"
                aria-hidden
            />
        );
    }
    return (
        <img
            src={src}
            alt={alt}
            className="h-12 w-20 shrink-0 rounded-r5 border border-black80 object-cover"
            onError={() => setFailed(true)}
        />
    );
}

function isOpenSpaceOfflineMessage(message: string): boolean {
    const text = message.toLowerCase();
    return (
        text.includes("no internet") ||
        text.includes("name resolution") ||
        text.includes("errno -3") ||
        text.includes("errno -2") ||
        text.includes("network is unreachable") ||
        text.includes("no route to host") ||
        text.includes("insta360 camera wi-fi") ||
        text.includes("insta360 camera wifi")
    );
}

function formatOpenSpaceJobMessage(raw: string | undefined, t: (key: string) => string): string {
    if (raw && isOpenSpaceOfflineMessage(raw)) {
        return t("openspace.uploadNoInternet");
    }
    return raw || t("openspace.uploadFailed");
}

function jobPercent(job: OpenSpaceUploadJob | null): number | null {
    if (!job) return null;
    const total = Number(job.bytes_total) || 0;
    const sent = Number(job.bytes_sent) || 0;
    if (total <= 0) return null;
    return Math.min(100, Math.round((sent / total) * 100));
}

function historyJobs(job: OpenSpaceUploadJob | null): OpenSpaceUploadJob[] {
    if (!job) return [];
    if (Array.isArray(job.history) && job.history.length > 0) {
        return job.history;
    }
    if (job.status && job.status !== "idle") return [job];
    return [];
}

const OpenSpaceSettingsPanel: FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { baseUrl, fsm, ingestState } = useInsta360Session();

    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [siteId, setSiteId] = useState("");
    const [sheetId, setSheetId] = useState("");
    const [deviceId, setDeviceId] = useState("");
    const [domain, setDomain] = useState(DEFAULT_DOMAIN);
    const [hasSecret, setHasSecret] = useState(false);
    const [hasPassword, setHasPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingSettings, setLoadingSettings] = useState(false);

    const [files, setFiles] = useState<Insta360RecordingFile[]>([]);
    const [selectedName, setSelectedName] = useState<string | null>(null);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState<string | null>(null);
    const [job, setJob] = useState<OpenSpaceUploadJob | null>(null);
    const lastJobStatus = useRef<string>("idle");
    const [cameraFiles, setCameraFiles] = useState<Insta360CameraFile[]>([]);
    const [cameraLoading, setCameraLoading] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [transferringName, setTransferringName] = useState<string | null>(null);
    const [logDownloading, setLogDownloading] = useState(false);
    const [playingName, setPlayingName] = useState<string | null>(null);
    const [playbackReady, setPlaybackReady] = useState(false);
    const [downloadingName, setDownloadingName] = useState<string | null>(null);

    const refreshList = useCallback(async () => {
        if (!baseUrl) {
            setFiles([]);
            setListError(null);
            return;
        }
        setListLoading(true);
        setListError(null);
        try {
            const res = await listInsta360Recordings(baseUrl);
            const next = res.files ?? [];
            setFiles(next);
            setSelectedName((prev) => {
                if (prev && next.some((f) => f.name === prev)) return prev;
                return next[0]?.name ?? null;
            });
        } catch (err) {
            setFiles([]);
            setListError(toUserFacingApiError(err, t("openspace.loadVideosFailed")));
        } finally {
            setListLoading(false);
        }
    }, [baseUrl, t]);

    const cameraIdle = fsm === "idle";
    const showCameraPanel = cameraIdle || fsm === "downloading" || Boolean(transferringName);

    const refreshCamera = useCallback(async () => {
        if (!baseUrl) {
            setCameraFiles([]);
            setCameraError(null);
            return;
        }
        if (fsm !== "idle") {
            setCameraError(null);
            if (fsm !== "downloading") setCameraFiles([]);
            return;
        }
        setCameraLoading(true);
        setCameraError(null);
        try {
            const res = await listInsta360CameraFiles(baseUrl);
            setCameraFiles(res.files);
            if (res.state) ingestState(res.state);
        } catch (err) {
            setCameraFiles([]);
            setCameraError(toUserFacingApiError(err, t("openspace.loadCameraFailed")));
        } finally {
            setCameraLoading(false);
        }
    }, [baseUrl, fsm, ingestState, t]);

    useEffect(() => {
        void refreshCamera();
    }, [refreshCamera]);

    const handleTransfer = async (filename: string) => {
        if (!baseUrl) return;
        setTransferringName(filename);
        ingestState("downloading");
        try {
            const res = await postInsta360CameraTransfer(baseUrl, filename);
            ingestState(res.state || "idle");
            addToast({ type: "success", message: t("openspace.transferOk", { name: filename }) });
            await Promise.all([refreshCamera(), refreshList()]);
        } catch (err) {
            ingestState("idle");
            addToast({
                type: "error",
                message: toUserFacingApiError(err, t("openspace.transferFailed")),
            });
            await refreshCamera();
        } finally {
            setTransferringName(null);
        }
    };

    useEffect(() => {
        if (!baseUrl) return;
        let cancelled = false;
        const load = async () => {
            setLoadingSettings(true);
            try {
                const remote = await getOpenSpaceSettings(baseUrl);
                if (cancelled) return;
                setClientId(remote.client_id || "");
                setUsername(remote.username || "");
                setSiteId(remote.site_id || "");
                setSheetId(remote.sheet_id || "");
                setDeviceId(remote.device_id || "");
                setDomain(remote.domain || DEFAULT_DOMAIN);
                setHasSecret(Boolean(remote.has_client_secret));
                setHasPassword(Boolean(remote.has_password));
                setClientSecret("");
                setPassword("");
            } catch {
                /* robot not reachable */
            } finally {
                if (!cancelled) setLoadingSettings(false);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [baseUrl]);

    useEffect(() => {
        void refreshList();
    }, [refreshList]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!baseUrl) return;
        setSaving(true);
        try {
            await putOpenSpaceSettings(baseUrl, {
                client_id: clientId.trim(),
                client_secret: clientSecret.trim(),
                username: username.trim(),
                password,
                site_id: siteId.trim(),
                sheet_id: sheetId.trim(),
                device_id: deviceId.trim(),
                domain: domain.trim() || DEFAULT_DOMAIN,
            });
            if (clientSecret.trim()) setHasSecret(true);
            if (password) setHasPassword(true);
            setClientSecret("");
            setPassword("");
            addToast({ type: "success", message: t("openspace.savedOnRobot") });
        } catch (err) {
            addToast({
                type: "error",
                message: toUserFacingApiError(err, t("openspace.saveFailed")),
            });
        } finally {
            setSaving(false);
        }
    };

    const applyJob = useCallback(
        (next: OpenSpaceUploadJob) => {
            setJob(next);
            const status = next.status || "idle";
            const prev = lastJobStatus.current;
            lastJobStatus.current = status;
            if (status === "done" && (prev === "queued" || prev === "running" || prev === "canceling") && next.filename) {
                addToast({
                    type: "success",
                    message: t("openspace.uploadOk", { name: next.filename }),
                });
            }
            if (status === "canceled" && (prev === "queued" || prev === "running" || prev === "canceling")) {
                addToast({ type: "success", message: t("openspace.uploadCanceled") });
            }
            if (status === "error" && (prev === "queued" || prev === "running")) {
                addToast({
                    type: "error",
                    message: formatOpenSpaceJobMessage(next.message, t),
                });
            }
        },
        [addToast, t]
    );

    useEffect(() => {
        if (!baseUrl) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const next = await getOpenSpaceUpload(baseUrl);
                if (!cancelled) applyJob(next);
            } catch {
                /* ignore poll failures while listing still works */
            }
        };
        void tick();
        const id = window.setInterval(() => void tick(), 1000);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [applyJob, baseUrl]);

    const handleUpload = async (filename: string) => {
        if (!baseUrl) return;
        try {
            const next = await postOpenSpaceUpload(baseUrl, {
                filename,
            });
            applyJob(next);
        } catch (err) {
            addToast({
                type: "error",
                message: toUserFacingApiError(err, t("openspace.uploadFailed")),
            });
        }
    };

    const handleCancel = async () => {
        if (!baseUrl) return;
        try {
            const next = await postOpenSpaceUploadCancel(baseUrl);
            applyJob(next);
        } catch (err) {
            addToast({
                type: "error",
                message: toUserFacingApiError(err, t("openspace.cancelFailed")),
            });
        }
    };

    const handleDownloadNetworkLog = async () => {
        if (!baseUrl) return;
        setLogDownloading(true);
        try {
            const { blob, filename } = await downloadOpenSpaceNetworkLog(baseUrl);
            const href = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = href;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(href);
        } catch (err) {
            addToast({
                type: "error",
                message: toUserFacingApiError(err, t("openspace.downloadNetworkLogFailed")),
            });
        } finally {
            setLogDownloading(false);
        }
    };

    const handlePlay = (filename: string) => {
        setPlaybackReady(false);
        setPlayingName(filename);
        setSelectedName(filename);
    };

    const handleClosePlayback = () => {
        setPlayingName(null);
        setPlaybackReady(false);
    };

    const handleDownloadVideo = async (filename: string) => {
        if (!baseUrl) return;
        setDownloadingName(filename);
        try {
            const blob = await fetchInsta360RecordingBlob(baseUrl, filename);
            triggerBrowserDownload(blob, filename);
        } catch (err) {
            addToast({
                type: "error",
                message: toUserFacingApiError(err, t("openspace.downloadFailed")),
            });
        } finally {
            setDownloadingName(null);
        }
    };

    const jobActive =
        job?.status === "queued" || job?.status === "running" || job?.status === "canceling";
    const canUpload =
        Boolean(baseUrl) &&
        !jobActive &&
        Boolean(clientId.trim()) &&
        hasSecret &&
        Boolean(username.trim()) &&
        hasPassword &&
        Boolean(siteId.trim()) &&
        Boolean(sheetId.trim()) &&
        Boolean(deviceId.trim());
    const percent = jobPercent(job);
    const pastJobs = historyJobs(job);

    return (
        <div className="flex max-w-3xl flex-col gap-8">
            <div>
                <p className="text-h4 font-semibold text-white">{t("openspace.title")}</p>
                <p className="mt-1 text-h5 text-black40">{t("openspace.description")}</p>
            </div>

            {!baseUrl ? (
                <p className="text-h5 text-black60">{t("openspace.connectFirst")}</p>
            ) : (
                <>
                    <form
                        onSubmit={(e) => void handleSubmit(e)}
                        className="flex flex-col gap-4 rounded-r10 border border-black80 bg-black90 p-4"
                    >
                        {loadingSettings ? (
                            <p className="text-h5 text-black40">{t("common:loading")}</p>
                        ) : null}
                        <label className="flex flex-col gap-1 text-h5 text-white">
                            {t("openspace.clientId")}
                            <input
                                value={clientId}
                                onChange={(e) => setClientId(e.target.value)}
                                className={INPUT_CLASS}
                                autoComplete="off"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-white">
                            {t("openspace.clientSecret")}
                            <input
                                type="password"
                                value={clientSecret}
                                onChange={(e) => setClientSecret(e.target.value)}
                                placeholder={hasSecret ? t("openspace.secretKept") : undefined}
                                className={INPUT_CLASS}
                                autoComplete="off"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-white">
                            {t("openspace.username")}
                            <input
                                type="email"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className={INPUT_CLASS}
                                autoComplete="username"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-white">
                            {t("openspace.password")}
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={hasPassword ? t("openspace.passwordKept") : undefined}
                                className={INPUT_CLASS}
                                autoComplete="new-password"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-white">
                            {t("openspace.siteId")}
                            <input
                                value={siteId}
                                onChange={(e) => setSiteId(e.target.value)}
                                className={INPUT_CLASS}
                                autoComplete="off"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-white">
                            {t("openspace.sheetId")}
                            <input
                                value={sheetId}
                                onChange={(e) => setSheetId(e.target.value)}
                                className={INPUT_CLASS}
                                autoComplete="off"
                            />
                        </label>
                        <p className="text-h5 text-black60">{t("openspace.sheetIdHint")}</p>
                        <label className="flex flex-col gap-1 text-h5 text-white">
                            {t("openspace.deviceId")}
                            <input
                                value={deviceId}
                                onChange={(e) => setDeviceId(e.target.value)}
                                placeholder={t("openspace.deviceIdPlaceholder")}
                                className={INPUT_CLASS}
                                autoComplete="off"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-h5 text-white">
                            {t("openspace.domain")}
                            <input
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                className={INPUT_CLASS}
                                autoComplete="off"
                            />
                        </label>
                        <p className="text-h5 text-black60">{t("openspace.domainHint")}</p>
                        <div className="flex justify-end">
                            <FunctionalButton
                                type="submit"
                                title={saving ? t("common:saving") : t("common:save")}
                                className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
                                titleClassName={ETHERNET_CONNECT_GREEN_TITLE_CLASS}
                                disabled={saving}
                            />
                        </div>
                    </form>

                    <section>
                        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <p className="text-h4 font-semibold text-white">
                                    {t("openspace.cameraTitle")}
                                </p>
                                <p className="mt-1 text-h5 text-black40">
                                    {t("openspace.cameraDescription")}
                                </p>
                            </div>
                            <FunctionalButton
                                title={t("common:refresh")}
                                className={NETWORK_ROW_CONNECT_BUTTON_CLASS}
                                titleClassName={NETWORK_ROW_CONNECT_TITLE_CLASS}
                                onClick={() => void refreshCamera()}
                                disabled={cameraLoading || !cameraIdle || Boolean(transferringName)}
                            />
                        </div>
                        {!showCameraPanel ? (
                            <p className="text-h5 text-black60">{t("openspace.cameraConnectHint")}</p>
                        ) : cameraError ? (
                            <p className="text-h5 text-red-400">{cameraError}</p>
                        ) : cameraLoading ? (
                            <p className="text-h5 text-black40">{t("common:loading")}</p>
                        ) : cameraFiles.length === 0 ? (
                            <p className="text-h5 text-black60">{t("openspace.cameraEmpty")}</p>
                        ) : (
                            <ul className="overflow-hidden rounded-r10 border border-black80">
                                {cameraFiles.map((file) => {
                                    const pulling = transferringName === file.name;
                                    return (
                                        <li
                                            key={file.name}
                                            className="flex flex-wrap items-center justify-between gap-3 border-b border-black80 p-3 last:border-b-0"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-h5 font-semibold text-white">
                                                    {file.name}
                                                </p>
                                                {file.on_robot ? (
                                                    <p className="mt-0.5 text-h5 text-black40">
                                                        {t("openspace.cameraOnRobot")}
                                                    </p>
                                                ) : null}
                                            </div>
                                            <FunctionalButton
                                                title={
                                                    pulling
                                                        ? t("openspace.transferring")
                                                        : t("openspace.transfer")
                                                }
                                                className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
                                                titleClassName={ETHERNET_CONNECT_GREEN_TITLE_CLASS}
                                                disabled={!cameraIdle || Boolean(transferringName)}
                                                onClick={() => void handleTransfer(file.name)}
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>

                    <section>
                        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <p className="text-h4 font-semibold text-white">
                                    {t("openspace.videosTitle")}
                                </p>
                                <p className="mt-1 text-h5 text-black40">
                                    {t("openspace.videosDescription")}
                                </p>
                            </div>
                            <div className="flex flex-col items-stretch gap-2 sm:items-end">
                                <FunctionalButton
                                    title={t("common:refresh")}
                                    className={NETWORK_ROW_CONNECT_BUTTON_CLASS}
                                    titleClassName={NETWORK_ROW_CONNECT_TITLE_CLASS}
                                    onClick={() => void refreshList()}
                                    disabled={listLoading}
                                />
                                <FunctionalButton
                                    title={t("openspace.downloadNetworkLog")}
                                    className={NETWORK_ROW_CONNECT_BUTTON_CLASS}
                                    titleClassName={NETWORK_ROW_CONNECT_TITLE_CLASS}
                                    onClick={() => void handleDownloadNetworkLog()}
                                    disabled={
                                        !baseUrl ||
                                        logDownloading ||
                                        !(Number(job?.network_log_count) > 0)
                                    }
                                />
                            </div>
                        </div>
                        <p className="mb-3 text-h5 text-black60">
                            {Number(job?.network_log_count) > 0
                                ? t("openspace.downloadNetworkLogHint")
                                : t("openspace.downloadNetworkLogEmpty")}
                        </p>
                        {jobActive || job?.status === "error" || job?.status === "canceled" ? (
                            <p className="mb-3 text-h5 text-black40">
                                {job?.status === "error"
                                    ? formatOpenSpaceJobMessage(job.message, t)
                                    : job?.status === "canceled"
                                      ? t("openspace.uploadCanceled")
                                      : t("openspace.uploadProgress", {
                                          name: job?.filename || "",
                                          stage: t(`openspace.stages.${job?.stage || "queued"}`),
                                          percent: percent ?? 0,
                                      })}
                                {job?.updated_at || job?.started_at
                                    ? ` · ${t("openspace.updatedAt", {
                                          time: formatJobTime(
                                              String(job?.updated_at || job?.started_at)
                                          ),
                                      })}`
                                    : ""}
                            </p>
                        ) : null}
                        {listError ? (
                            <p className="text-h5 text-red-400">{listError}</p>
                        ) : listLoading ? (
                            <p className="text-h5 text-black40">{t("common:loading")}</p>
                        ) : files.length === 0 ? (
                            <div className="rounded-r10 border border-dashed border-black80 px-4 py-8 text-center">
                                <p className="text-h5 font-semibold text-white">
                                    {t("openspace.noVideosTitle")}
                                </p>
                                <p className="mt-1 text-h5 text-black40">
                                    {t("openspace.noVideosHint")}
                                </p>
                            </div>
                        ) : (
                            <ul className="overflow-hidden rounded-r10 border border-black80">
                                {files.map((file) => {
                                    const selected = file.name === selectedName;
                                    const uploading = jobActive && job?.filename === file.name;
                                    return (
                                        <li
                                            key={file.name}
                                            className={`flex flex-wrap items-center justify-between gap-3 border-b border-black80 p-3 last:border-b-0 ${
                                                selected ? "bg-black80" : "bg-transparent"
                                            }`}
                                        >
                                            {baseUrl ? (
                                                <button
                                                    type="button"
                                                    className="shrink-0"
                                                    onClick={() => handlePlay(file.name)}
                                                    aria-label={t("openspace.play")}
                                                >
                                                    <InsvPreview
                                                        baseUrl={baseUrl}
                                                        filename={file.name}
                                                        alt={file.name}
                                                    />
                                                </button>
                                            ) : null}
                                            <button
                                                type="button"
                                                className="min-w-0 flex-1 text-left"
                                                onClick={() => setSelectedName(file.name)}
                                            >
                                                <p className="truncate text-h5 font-semibold text-white">
                                                    {file.name}
                                                </p>
                                                <p className="mt-0.5 text-h5 text-black40">
                                                    {formatInsvSize(file.size_bytes)}
                                                    {file.modified_utc
                                                        ? ` · ${formatJobTime(file.modified_utc)}`
                                                        : ""}
                                                </p>
                                            </button>
                                            <div className="flex shrink-0 flex-wrap items-center gap-2">
                                                <FunctionalButton
                                                    title={t("openspace.play")}
                                                    className={NETWORK_ROW_CONNECT_BUTTON_CLASS}
                                                    titleClassName={NETWORK_ROW_CONNECT_TITLE_CLASS}
                                                    onClick={() => handlePlay(file.name)}
                                                />
                                                <FunctionalButton
                                                    title={
                                                        downloadingName === file.name
                                                            ? t("common:loading")
                                                            : t("openspace.downloadVideo")
                                                    }
                                                    className={NETWORK_ROW_CONNECT_BUTTON_CLASS}
                                                    titleClassName={NETWORK_ROW_CONNECT_TITLE_CLASS}
                                                    disabled={downloadingName === file.name}
                                                    onClick={() => void handleDownloadVideo(file.name)}
                                                />
                                                {uploading ? (
                                                    <FunctionalButton
                                                        title={
                                                            job?.status === "canceling"
                                                                ? t("openspace.canceling")
                                                                : t("common:cancel")
                                                        }
                                                        className={NETWORK_ROW_DISCONNECT_BUTTON_CLASS}
                                                        titleClassName={NETWORK_ROW_DISCONNECT_TITLE_CLASS}
                                                        disabled={job?.status === "canceling"}
                                                        onClick={() => void handleCancel()}
                                                    />
                                                ) : (
                                                    <FunctionalButton
                                                        title={t("common:upload")}
                                                        className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
                                                        titleClassName={ETHERNET_CONNECT_GREEN_TITLE_CLASS}
                                                        disabled={!canUpload || jobActive}
                                                        onClick={() => void handleUpload(file.name)}
                                                    />
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {!canUpload && files.length > 0 ? (
                            <p className="mt-3 text-h5 text-black60">{t("openspace.uploadNeedsSettings")}</p>
                        ) : null}
                    </section>

                    <section>
                        <div className="mb-3">
                            <p className="text-h4 font-semibold text-white">
                                {t("openspace.historyTitle")}
                            </p>
                            <p className="mt-1 text-h5 text-black40">
                                {t("openspace.historyDescription")}
                            </p>
                        </div>
                        {pastJobs.length === 0 ? (
                            <p className="text-h5 text-black60">{t("openspace.historyEmpty")}</p>
                        ) : (
                            <ul className="overflow-hidden rounded-r10 border border-black80">
                                {pastJobs.map((entry, index) => {
                                    const pct = jobPercent(entry);
                                    const status = entry.status || "";
                                    const stageKey = entry.stage || status || "queued";
                                    const stageLabel = t(`openspace.stages.${stageKey}`, {
                                        defaultValue: stageKey,
                                    });
                                    const tone =
                                        status === "error"
                                            ? "text-red-400"
                                            : status === "done"
                                              ? "text-white"
                                              : "text-black40";
                                    const detail =
                                        status === "error"
                                            ? formatOpenSpaceJobMessage(entry.message, t)
                                            : status === "canceled"
                                              ? t("openspace.uploadCanceled")
                                              : status === "done"
                                                ? t("openspace.uploadOk", {
                                                      name: entry.filename || "",
                                                  })
                                                : t("openspace.uploadProgress", {
                                                      name: entry.filename || "",
                                                      stage: stageLabel,
                                                      percent: pct ?? 0,
                                                  });
                                    return (
                                        <li
                                            key={entry.id || `${entry.filename}-${entry.started_at}-${index}`}
                                            className="border-b border-black80 p-3 last:border-b-0"
                                        >
                                            <p className="truncate text-h5 font-semibold text-white">
                                                {entry.filename || "—"}
                                            </p>
                                            <p className={`mt-0.5 text-h5 ${tone}`}>{detail}</p>
                                            <p className="mt-0.5 text-h5 text-black60">
                                                {t("openspace.updatedAt", {
                                                    time:
                                                        formatJobTime(
                                                            String(
                                                                entry.updated_at ||
                                                                    entry.started_at ||
                                                                    ""
                                                            )
                                                        ) || "—",
                                                })}
                                                {entry.capture_id
                                                    ? ` · ${t("openspace.captureId", { id: entry.capture_id })}`
                                                    : ""}
                                            </p>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                </>
            )}
            {playingName && baseUrl ? (
                <AnimatedModal
                    visible
                    onClose={handleClosePlayback}
                    className="w-full max-w-3xl rounded-r10 bg-black100 p-4"
                    zIndexClassName="z-[80]"
                >
                    <div className="mb-3 flex items-start justify-between gap-3">
                        <p className="min-w-0 truncate text-h4 font-semibold text-white">
                            {playingName}
                        </p>
                        <FunctionalButton
                            title={t("common:close")}
                            className={NETWORK_ROW_CONNECT_BUTTON_CLASS}
                            titleClassName={NETWORK_ROW_CONNECT_TITLE_CLASS}
                            onClick={handleClosePlayback}
                        />
                    </div>
                    <div className="relative overflow-hidden rounded-r5 bg-black">
                        <video
                            key={playingName}
                            className="max-h-[70vh] w-full bg-black object-contain"
                            src={getInsta360PlaybackUrl(baseUrl, playingName)}
                            poster={getInsta360PreviewUrl(baseUrl, playingName)}
                            controls
                            playsInline
                            preload="metadata"
                            autoPlay
                            onCanPlay={() => setPlaybackReady(true)}
                            onError={() => {
                                addToast({
                                    type: "error",
                                    message: t("openspace.playbackFailed"),
                                });
                            }}
                        >
                            {t("openspace.playbackFailed")}
                        </video>
                        {!playbackReady ? (
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/70 px-4">
                                <p className="text-center text-h5 text-white">
                                    {t("openspace.playbackPreparing")}
                                </p>
                            </div>
                        ) : null}
                    </div>
                </AnimatedModal>
            ) : null}
        </div>
    );
};

export default OpenSpaceSettingsPanel;
